'use client';

import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useParams } from 'next/navigation';

// Utils
import { formatDate, hasProperty, isArray, parseCertificationDataToStore, parseEducationDataToStore } from '@shared/utils';
import { convertAvatarUrlToBase64 } from '@/utils/image';
import { showErrorToast } from '@/utils/errorHandlers';

// Stores
import { useResumeActions } from '@shared/stores';

// Constants
import { RESUME_MESSAGE, ROUTES, STATUS_CODE } from '@shared/constants';
import { CONTENT_MESSAGE, ERROR_MESSAGES, IMAGES } from '@/constants';

// Components
import { ErrorContent } from '@/components';

// Shared
import { ActivityType, PersonalInformation, PreviewImage } from '@shared/types';
import { LoadingIndicator } from '@shared/ui';

// Actions
import { getUserResumeById } from '@/actions/client/resume';

// Hooks
import { useFirebase } from './firebase';
import { useWebSocketMessage } from '@/hooks/useWebSocket';
import { Employee } from '@/types';
const ResumeContext = createContext({});
interface ResumeProviderProps {
  children: ReactNode;
}
export const ResumeProvider = ({
  children
}: ResumeProviderProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const {
    initializeStore,
    updatePersonalInfo,
    updateCertification,
    updateEducation,
    updateSkills
  } = useResumeActions();
  const {
    getToken,
    user
  } = useFirebase();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  useEffect(() => {
    if (isInitialized) {
      return;
    }
    const initialize = async () => {
      try {
        const token = await getToken();
        const resumeResponseData = await getUserResumeById({
          resumeId: id,
          requestConfig: {
            token
          }
        });

        // Get the list of resumes from the user
        const {
          data: resumeData,
          error: resumeError,
          status: resumeStatusCode
        } = resumeResponseData;
        if (resumeStatusCode === STATUS_CODE.NOT_FOUND || resumeStatusCode === STATUS_CODE.FORBIDDEN) {
          setIsNotFound(true);
          setIsInitialized(true);
          return;
        }
        if (resumeStatusCode !== STATUS_CODE.OK && !resumeData) {
          showErrorToast(RESUME_MESSAGE.FAILED_TO_FETCH_RESUMES(String(resumeError)));
          setIsInitialized(true);
          return;
        }
        let info: (PersonalInformation & PreviewImage) | null = null;
        if (resumeData && hasProperty(resumeData.info, 'name')) {
          // Process to convert the avatar to base64
          const avatarBase64 = await convertAvatarUrlToBase64(resumeData.info?.avatar || '');
          info = resumeData.info && {
            ...resumeData.info,
            email: resumeData.id || resumeData.info?.email || '',
            previewImageBase64: avatarBase64,
            avatar: resumeData.info.avatar || ''
          };
        }

        // Update the store
        initializeStore({
          ...resumeData,
          id,
          info
        });
        setIsInitialized(true);
      } catch (error) {
        showErrorToast(error);
      }
    };
    if (id) {
      initialize();
    }

    // Don't need to put "getToken" in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeStore, isInitialized, id]);
  useWebSocketMessage<{
    data: Employee;
  }>(async message => {
    if (!message?.channel || !message?.payload) return;
    switch (message.channel) {
      case ActivityType.EMPLOYEE_UPDATE_INFO:
        {
          if (!('data' in message.payload)) return;
          const employeeInfoUpdate = message.payload.data;
          if (employeeInfoUpdate.info && user?.email?.includes(employeeInfoUpdate.info.username || '')) {
            updatePersonalInfo({
              ...employeeInfoUpdate.info,
              birthday: formatDate(employeeInfoUpdate.info.birthday)
            });
            updateCertification(isArray(employeeInfoUpdate.certs) ? parseCertificationDataToStore(employeeInfoUpdate.certs) : []);
            updateSkills(isArray(employeeInfoUpdate.skills) ? employeeInfoUpdate.skills : []);
            updateEducation(isArray(employeeInfoUpdate.education) ? parseEducationDataToStore(employeeInfoUpdate.education) : []);
          }
          break;
        }
    }
  });
  return <ResumeContext.Provider value={{}} data-sentry-element="unknown" data-sentry-component="ResumeProvider" data-sentry-source-file="resume.tsx">
      {!isInitialized ? <div className="flex-1 flex-center min-h-form-layout">
          <LoadingIndicator size="lg" color="primary" center />
        </div> : isNotFound ? <div className="flex-1 min-h-form-layout bg-primary-100">
          {/* Content */}
          <ErrorContent imageSrc={IMAGES.LOGO_NOTFOUND} imageAlt="Logo page not found" imageClass="w-logo-notfound-mobi h-logo-notfound-mobi md:w-logo-notfound md:h-logo-notfound" buttonLabel="Go back" buttonLink={ROUTES.HOME} title={ERROR_MESSAGES.PAGE_NOT_FOUND} subtitle={CONTENT_MESSAGE.PAGE_NOT_FOUND} className="py-10 md:py-16" />
        </div> : children}
    </ResumeContext.Provider>;
};
export const useResumeContext = () => {
  const context = useContext(ResumeContext);
  if (context === undefined) {
    throw new Error('useResumeContext must be used within a ResumeProvider');
  }
  return context;
};